import axios from 'axios';
import { msalInstance } from '../auth';
import { getItem, setItem } from '../storage';
import { subDays, format, parse, parseISO, isMatch } from 'date-fns';
import { convertRelativeToDate } from '../../services/utils';

import { ReactComponent as DocumentIcon } from '../../icons/item-icons/document.svg';
import { ReactComponent as GenerationAssetIcon } from '../../icons/item-icons/document.svg';
import { ReactComponent as FinancialInstrumentIcon } from '../../icons/item-icons/financial-instrument.svg';
import { ReactComponent as MarketPriceModelIcon } from '../../icons/item-icons/market-price-model.svg';
import { ReactComponent as GasStorageIcon } from '../../icons/item-icons/gas-storage.svg';
import { ReactComponent as GasTransportIcon } from '../../icons/item-icons/gas-transport.svg';
import { ReactComponent as RetailPricingIcon } from '../../icons/item-icons/retail-pricing.svg';
import { ReactComponent as ForwardCurveIcon } from '../../icons/item-icons/forward-curve.svg';
import { ReactComponent as LoadIcon } from '../../icons/item-icons/load.svg';
import { ReactComponent as HydroIcon } from '../../icons/item-icons/hydro.svg';
import { ReactComponent as TransmissionLossIcon } from '../../icons/item-icons/transmission-loss.svg';
import { ReactComponent as DsmIcon } from '../../icons/item-icons/dsm.svg';
import { ReactComponent as WindTurbineIcon } from '../../icons/item-icons/wind-turbine.svg';
import { ReactComponent as FixedCostIcon } from '../../icons/item-icons/fixed-cost.svg';
import { ReactComponent as GasLoadIcon } from '../../icons/item-icons/gas-load.svg';
import { ReactComponent as HydroProjectIcon } from '../../icons/item-icons/hydro-project.svg';
import { ReactComponent as AccountsIcon } from '../../icons/item-icons/accounts.svg';
import { ReactComponent as AncillaryReserveIcon } from '../../icons/item-icons/ancillary-reserve.svg';
import { ReactComponent as EconomicAssumptionIcon } from '../../icons/item-icons/economic-assumption.svg';
import { ReactComponent as InclusiveResourceIcon } from '../../icons/item-icons/inclusive-resource.svg';
import { ReactComponent as ExclusiveResourceIcon } from '../../icons/item-icons/exclusive-resource.svg';
import { ReactComponent as ScheduledMaintenanceOptimizationIcon } from '../../icons/item-icons/scheduled-maintenance-optimization.svg';
import { ReactComponent as ResourcePlanningMastIcon } from '../../icons/item-icons/resource-planning-mast.svg';
import { ReactComponent as DeterministicRunSettingsIcon } from '../../icons/item-icons/deterministic-run-settings.svg';
import { ReactComponent as ForwardCurveConstraintsIcon } from '../../icons/item-icons/forward-curve-constraints.svg';
import { ReactComponent as RenewableEnergyCreditIcon } from '../../icons/item-icons/renewable-energy-credit.svg';
import { ReactComponent as HedgeModelIcon } from '../../icons/item-icons/hedge-model.svg';
import { ReactComponent as TradableProductIcon } from '../../icons/item-icons/tradable-product.svg';
import { ReactComponent as StructuredProductIcon } from '../../icons/item-icons/structured-product.svg';
import { ReactComponent as CaesIcon } from '../../icons/item-icons/caes.svg';
import { ReactComponent as BatteryIcon } from '../../icons/item-icons/battery.svg';
import { ReactComponent as ShapeSetsIcon } from '../../icons/item-icons/shape-sets.svg';
import { ReactComponent as GlobalConstraintsIcon } from '../../icons/item-icons/global-constraints.svg';
import { ReactComponent as FunctionalForms1Icon } from '../../icons/item-icons/functional-forms-1.svg';
import { ReactComponent as TransmissionLineIcon } from '../../icons/item-icons/transmission-line.svg';
import { ReactComponent as ReserveIcon } from '../../icons/item-icons/reserve.svg';
import { ReactComponent as NetDemandLimitsIcon } from '../../icons/item-icons/net-demand-limits.svg';
import { ReactComponent as SolarIcon } from '../../icons/item-icons/solar.svg';

export const mappedItemTypes = {
    DEMOTIEPOINT: {
        id: -1,
        description: 'Demotiepoint',
        icon: DocumentIcon,
    },
    GENERATION_ASSET: {
        id: 2,
        description: 'Generation Asset',
        icon: GenerationAssetIcon,
    },
    FINANCIAL_INSTRUMENT: {
        id: 3,
        description: 'Financial Instrument',
        icon: FinancialInstrumentIcon,
    },
    MARKET_PRICE_MODEL: {
        id: 4,
        description: 'Market Price Model',
        icon: MarketPriceModelIcon,
    },
    GAS_STORAGE: {
        id: 6,
        description: 'Gas Storage',
        icon: GasStorageIcon,
    },
    GAS_TRANSPORT: {
        id: 7,
        description: 'Gas Transport',
        icon: GasTransportIcon,
    },
    RETAIL_PRICING: {
        id: 8,
        description: 'Retail Pricing',
        icon: RetailPricingIcon,
    },
    FORWARD_CURVE: {
        id: 11,
        description: 'Forward Curve',
        icon: ForwardCurveIcon,
    },
    LOAD: {
        id: 12,
        description: 'Load',
        icon: LoadIcon,
    },
    HYDRO: {
        id: 13,
        description: 'Hydro',
        icon: HydroIcon,
    },
    TRANSMISSION_LOSS: {
        id: 14,
        description: 'Transmission Loss',
        icon: TransmissionLossIcon,
    },
    PHYSICAL_INSTRUMENT: {
        id: 15,
        description: 'Physical Instrument',
        icon: DocumentIcon,
    },
    DSM: {
        id: 16,
        description: 'DSM',
        icon: DsmIcon,
    },
    WIND_ASSET: {
        id: 17,
        description: 'Wind Asset',
        icon: WindTurbineIcon,
    },
    FIXED_COST: {
        id: 18,
        description: 'Fixed Cost',
        icon: FixedCostIcon,
    },
    TRANSMISSION_LINE: {
        id: 19,
        description: 'Transmission Line',
        icon: TransmissionLineIcon,
    },
    GAS_LOAD: {
        id: 20,
        description: 'Gas Load',
        icon: GasLoadIcon,
    },
    HYDRO_PROJECT: {
        id: 21,
        description: 'Hydro Project',
        icon: HydroProjectIcon,
    },
    ACCOUNTS: {
        id: 22,
        description: 'Accounts',
        icon: AccountsIcon,
    },
    ANCILLARY_RESERVE: {
        id: 23,
        description: 'Ancillary Reserve',
        icon: AncillaryReserveIcon,
    },
    LIABILITY: {
        id: 24,
        description: 'Liability',
        icon: ResourcePlanningMastIcon,
    },
    ECONOMIC_ASSUMPTION: {
        id: 25,
        description: 'Economic Assumption',
        icon: EconomicAssumptionIcon,
    },
    NEW_PROJECT_DETAILS: {
        id: 26,
        description: 'New Project Details',
        icon: ResourcePlanningMastIcon,
    },
    PLANNING_CONSTRAINT: {
        id: 27,
        description: 'Planning Constraint',
        icon: ResourcePlanningMastIcon,
    },
    INCLUSIVE_RESOURCE: {
        id: 28,
        description: 'Inclusive Resource',
        icon: InclusiveResourceIcon,
    },
    EXCLUSIVE_RESOURCE: {
        id: 29,
        description: 'Exclusive Resource',
        icon: ExclusiveResourceIcon,
    },
    DETERMINISTIC_RUN_SETTINGS: {
        id: 30,
        description: 'Deterministic Run Settings',
        icon: DeterministicRunSettingsIcon,
    },
    SHEDULED_MAINTENANCE_OPTIMIZATION: {
        id: 31,
        description: 'Scheduled Maintenance Optimization',
        icon: ScheduledMaintenanceOptimizationIcon,
    },
    EXISTING_ASSET_BASE: {
        id: 33,
        description: 'Existing Asset Base',
        icon: ResourcePlanningMastIcon,
    },
    PLANNING_OBJECTIVE_FUNCTION: {
        id: 34,
        description: 'Planning Objective Function',
        icon: ResourcePlanningMastIcon,
    },
    PLANNING_CONSTRAINT_SET: {
        id: 35,
        description: 'Planning Constraint Set',
        icon: ResourcePlanningMastIcon,
    },
    DETERMINISTIC_SETTINGS: {
        id: 40,
        description: 'Deterministic Settings',
        icon: DeterministicRunSettingsIcon,
    },
    FORWARD_CURVE_CONSTRAINTS: {
        id: 41,
        description: 'Forward Curve Constraints',
        icon: ForwardCurveConstraintsIcon,
    },
    RENEWABLE_ENERGY_CREDIT: {
        id: 42,
        description: 'Renewable Energy Credit',
        icon: RenewableEnergyCreditIcon,
    },
    HEDGE: {
        id: 43,
        description: 'Hedge Model',
        icon: HedgeModelIcon,
    },
    TRADABLE_PRODUCT: {
        id: 44,
        description: 'Tradable Product',
        icon: TradableProductIcon,
    },
    STRUCTURED_PRODUCT: {
        id: 45,
        description: 'Structured Product',
        icon: StructuredProductIcon,
    },
    RESERVE_FOLLOWING: {
        id: 46,
        description: 'Reserve Following',
        icon: ReserveIcon,
    },
    RESERVE_CONTINGENCY: {
        id: 47,
        description: 'Reserve Contingency',
        icon: ReserveIcon,
    },
    BATTERY: {
        id: 48,
        description: 'Battery',
        icon: BatteryIcon,
    },
    CAES: {
        id: 49,
        description: 'CAES',
        icon: CaesIcon,
    },
    BATTERY_STORAGE: {
        id: 55,
        description: 'Battery Storage',
        icon: BatteryIcon,
    },
    SHAPE_SETS: {
        id: 65,
        description: 'Shape Sets',
        icon: ShapeSetsIcon,
    },
    GLOBAL_CONSTRAINTS: {
        id: 75,
        description: 'Global Constraints',
        icon: GlobalConstraintsIcon,
    },
    FUNCTIONAL_FORMS: {
        id: 85,
        description: 'Functional Forms',
        icon: FunctionalForms1Icon,
    },
    TX_NODE: {
        id: 90,
        description: 'TX Node',
        icon: TransmissionLineIcon,
    },
    TX_LINE: {
        id: 92,
        description: 'TX Line',
        icon: TransmissionLineIcon,
    },
    ECONOMIC_RESERVE: {
        id: 95,
        description: 'Economic Reserve',
        icon: ReserveIcon,
    },
    NET_DEMAND_LIMITS: {
        id: 97,
        description: 'Net Demand Limits',
        icon: NetDemandLimitsIcon,
    },
    SOLAR_ASSET: {
        id: 100,
        description: 'Solar Asset',
        icon: SolarIcon,
    },
};

// function that prohibits the enter key down on item page inputs
export const checkItemInputEvent = (e) => {
    e.stopPropagation();

    if (e.type === 'keydown' && e.keyCode === 13) {
        e.preventDefault();
    }
};

// function that flats item name and given sub-items' descriptions into a single object to help control the edit view
export const flatItemDescriptions = (definitions, itemId) => {
    const descriptionsConfig = {};

    definitions.forEach((def) => {
        descriptionsConfig[`${def.id}`] = { isActive: false, penDisabled: false };
    });

    if (itemId) {
        descriptionsConfig[`${parseInt(itemId)}`] = {
            isActive: false,
            penDisabled: false,
        };
    }

    return descriptionsConfig;
};

export const getItemProperties = async (itemId) => {
    const response = await axios.get(`/items/${itemId}/properties`);
    return response.data;
};

export const createItem = (payload) => {
    return axios.post('/items/create', payload);
};

export const sortItemsByDescription = (items) => {
    return items.slice().sort((a, b) => a.description.localeCompare(b.description));
};

export const updateItem = (id, payload) => axios.put(`/items/${id}/properties`, payload);

export const editItem = (id, payload) => axios.put(`/items/${id}/modify`, payload);

export const addItemNextToParent = (stack, parentId, temporaryNode) => {
    const newStack = [...stack];
    const parentNodeIndex = newStack.findIndex((node) => node.id === parentId);
    newStack.splice(parentNodeIndex + 1, 0, temporaryNode);

    return newStack;
};

export const createDateAsUTC = (date) => {
    return new Date(
        Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            date.getMinutes(),
            date.getSeconds(),
            date.getMilliseconds()
        )
    );
};

export const stripTimezoneIndicator = (date) => {
    const tempISOString = typeof date === 'string' ? date : date.toISOString();
    // "Z" in ISO format tells parseISO that we have UTC time, without Z it is treated as local time without conversion
    return tempISOString.slice(0, -1);
};

export const setFiltersConfig = (config, item) => {
    const accounts = msalInstance.getAllAccounts();

    if (accounts.length) {
        const itemFilters = `${item}Filters`;
        const filters = getItem(itemFilters) || {};

        setItem(itemFilters, {
            ...filters,
            [accounts[0].localAccountId]: config,
        });
    }
};

export const getFiltersConfig = (item) => {
    const accounts = msalInstance.getAllAccounts();

    if (accounts.length) {
        const accountId = accounts[0].localAccountId;
        const filters = getItem(`${item}Filters`);

        if (filters && filters[accountId]) {
            let { startDateFrom, startDateTo, updateDateFrom, updateDateTo, ...rest } = filters[accountId];

            startDateFrom = startDateFrom && parseISO(startDateFrom);
            startDateTo = startDateTo && parseISO(startDateTo);
            updateDateFrom = updateDateFrom && parseISO(updateDateFrom);
            updateDateTo = updateDateTo && parseISO(updateDateTo);

            return {
                startDateFrom,
                startDateTo,
                updateDateFrom,
                updateDateTo,
                ...rest,
            };
        }
    }

    return null;
};

export const systemDefaultFilter = {
    startDateFrom: new Date(2015, 0, 1),
    startDateTo: new Date(2050, 11, 31),
    daysFrom: -21,
    daysTo: 0,
    toggleDaysFrom: false,
    toggleDaysTo: false,
    filterType: 1, // latest (1) | latest as of (2) | range (3)
    updateDateFrom: subDays(new Date(), 20),
    updateDateTo: new Date(),
};

export const getDataFilters = (namespacePrefix) => {
    const userPreferences = getFiltersConfig(namespacePrefix);

    if (userPreferences) {
        const filters = getFiltersConfig(namespacePrefix);

        return parseDataFilters(filters);
    } else {
        return {
            startDateFrom: systemDefaultFilter.startDateFrom,
            startDateTo: systemDefaultFilter.startDateTo,
            filterType: systemDefaultFilter.filterType,
            updateDateFrom: systemDefaultFilter.updateDateFrom,
            updateDateTo: systemDefaultFilter.updateDateTo,
        };
    }
};

export const parseDataFilters = (filters) => {
    return {
        // @todo add guards against partially set filters like this { toggleDaysFrom: true, daysFrom: undefined }
        startDateFrom: filters.toggleDaysFrom ? convertRelativeToDate(filters.daysFrom) : filters.startDateFrom,
        startDateTo: filters.toggleDaysTo ? convertRelativeToDate(filters.daysTo) : filters.startDateTo,
        filterType: parseInt(filters.filterType, 10),
        updateDateFrom: filters.updateDateFrom || new Date(),
        updateDateTo: filters.updateDateTo || new Date(),
    };
};

export const hasAppliedCustomFilters = (filters) => {
    return (
        filters.startDateFrom.getTime() !== systemDefaultFilter.startDateFrom.getTime() ||
        filters.startDateTo.getTime() !== systemDefaultFilter.startDateTo.getTime() ||
        filters.filterType !== systemDefaultFilter.filterType ||
        filters.updateDateFrom.getTime() !== systemDefaultFilter.updateDateFrom.getTime() ||
        filters.updateDateTo.getTime() !== systemDefaultFilter.updateDateTo.getTime()
    );
};

export const formatChartData = (data) => {
    return data.map((el) => [new Date(el.dateTimeAxis).getTime(), el.avgLoad]);
};

export const uploadFile = async (itemType, itemId, file) => {
    const config = {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    };
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(`/${itemType}/${itemId}/import`, formData, config);
    return response.data;
};

export const exportItemData = async (item, itemId, payload) => {
    const config = { responseType: 'blob' };
    const response = await axios.post(`/${item}/${itemId}/export`, payload, config);

    let filename = 'export.xlsx';
    if (response.headers['content-disposition'] !== undefined) {
        filename = response.headers['content-disposition'].match('(?<=filename=)(.*)(?=;)')[0];
    }

    return { file: response.data, name: filename };
};

export const enUSFormat = 'MM/dd/yyyy hh:mm:ss a';
export const enUSFormatExcludingSeconds = 'MM/dd/yyyy h:mm aa';
export const enUSFormatExcludingTime = 'MM/dd/yyyy';

export const lenientParse = (dateString, isUTC = true) => {
    if (!dateString || dateString === 'Invalid Date') {
        return new Date('Invalid Date');
    }

    const fromISOString = parseISO(isUTC ? stripTimezoneIndicator(dateString) : dateString);
    if (!Number.isNaN(fromISOString.getTime())) {
        return fromISOString;
    }

    // fallback to en-US format if parseISO fails
    const fromDateString = parse(isUTC ? stripTimezoneIndicator(dateString) : dateString, enUSFormat, new Date());
    if (!Number.isNaN(fromDateString.getTime())) {
        return fromDateString;
    }

    // fallback to new Date because parse fails at eg. (2015-01-13 0:00:00)
    return new Date(dateString);
};

export const formatDate = (date, dateFormat = enUSFormat) => {
    if (Number.isNaN(date.getTime())) {
        return 'Invalid date';
    }

    return format(date, dateFormat);
};

export const dateValueFormatter = (params, format) => {
    const date = params.value ? lenientParse(params.value) : null;

    return date ? formatDate(date, format) : null;
};

export const checkValidityBeforeSave = (value) => {
    return (
        dateValueFormatter({
            value,
        }) !== 'Invalid date'
    );
};

export const removeTrailingNewLine = (data) => {
    return data.map((row) => {
        const pair = {};

        for (const key in row) {
            if (key === '__parsed_extra') continue;
            pair[key] = row[key].replace(/(?:\\[rn]|[\r\n]+)+/g, '');
        }

        return pair;
    });
};

export const validateFields = (fields) => {
    return fields.map((field) => {
        if (!field.value && !field.isRequired) {
            // not all fields are required by default
            return false;
        } else if (!field.value && field.isRequired) {
            // field is required but no value
            return true;
        } else {
            // enteres in specific type check according to the predefined schema
            if (field.type === 'date') {
                return !isMatch(field.value, enUSFormat);
            } else if (field.type === 'number') {
                return isNaN(field.value);
            } else if (field.type === 'bool-number') {
                return isNaN(field.value) || field.value > 1 || field.value < 0;
            } else if (field.type === 'string') {
                if (!field.constraints) {
                    return typeof field.value !== 'string';
                }

                return typeof value !== 'string' && !field.constraints.find((str) => str === field.value);
            } else {
                return false;
            }
        }
    });
};

export const isNumberCellEmpty = (value) => (value ? Number(value) : null);

export const downloadFile = (fileInfo) => {
    const url = window.URL.createObjectURL(new Blob([fileInfo.file]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileInfo.name);
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
};

export const validateItemOwnershipPercentage = (input) => {
    const isNumeric = (number) => {
        return !isNaN(parseFloat(number)) && isFinite(number);
    };

    return (
        input.includes('+') ||
        (input.includes('.') && input.split('.')[1] === '') ||
        !isNumeric(input) ||
        Number(input) < 0 ||
        Number(input) > 100
    );
};

export const scrollToActiveItem = (sidebarRef) => {
    setTimeout(() => {
        sidebarRef.current.querySelector('.active-item').scrollIntoView();
    }, 0);
};
